import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Plus, Star, Lock, BookOpen, ArrowLeft, X, Crown } from 'lucide-react'
import { useAuth } from '../hooks/useAuth.jsx'
import { useTimers, formatTime } from '../hooks/useTimers.jsx'
import { FoodIllustration } from '../components/SVGIllustrations.jsx'
import { paper, leather, fonts, shadows } from '../styles/theme.js'
import UpgradeModal from '../components/UpgradeModal.jsx'
import ImportModal from '../components/ImportModal.jsx'

export default function RecipeList() {
  const { user, authFetch, isSubscriber } = useAuth()
  const { timers } = useTimers()
  const navigate = useNavigate()

  const [recipes, setRecipes] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [upgradeModal, setUpgradeModal] = useState(null) // null | { recipeTitle }
  const [importModal, setImportModal] = useState(false)

  const activeTimers = timers.filter(t => t.running || t.done)

  const loadRecipes = useCallback(async () => {
    try {
      const res = await authFetch('/api/recipes')
      if (res.ok) { const d = await res.json(); setRecipes(d) }
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }, [authFetch])

  useEffect(() => { loadRecipes() }, [loadRecipes])

  const filtered = recipes.filter(r =>
    r.title.toLowerCase().includes(search.toLowerCase()) ||
    (r.tags || []).some(t => t.toLowerCase().includes(search.toLowerCase()))
  )

  const toggleFavorite = async (id, e) => {
    e.stopPropagation()
    try {
      await authFetch(`/api/recipes/${id}/favorite`, { method: 'PATCH' })
      setRecipes(prev => prev.map(r => r.id === id ? { ...r, favorite: !r.favorite } : r))
    } catch {}
  }

  const handleCardClick = (recipe, index) => {
    if (!isSubscriber && index > 0) {
      setUpgradeModal({ recipeTitle: recipe.title })
    } else {
      navigate(`/recipes/${recipe.id}`)
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: paper.bg, display: 'flex', flexDirection: 'column' }}>
      {/* Active timers banner */}
      {activeTimers.length > 0 && (
        <div style={{
          background: paper.ink, color: paper.bgWhite, padding: '8px 20px',
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          fontFamily: fonts.serif, fontSize: 14,
        }}>
          <span style={{ color: paper.accentWarm, fontWeight: 600 }}>⏱ Active Timers:</span>
          {activeTimers.map(t => (
            <button
              key={t.id}
              onClick={() => t.recipeId && navigate(`/recipes/${t.recipeId}`)}
              style={{
                background: t.done ? paper.accentWarm : paper.inkLight,
                border: 'none', borderRadius: 12, padding: '3px 10px',
                color: 'white', cursor: 'pointer', fontFamily: fonts.mono, fontSize: 13,
                animation: t.done ? 'pulse 0.8s ease-in-out infinite' : 'none',
              }}
            >
              {t.label}: {t.done ? 'Done!' : formatTime(t.remaining)}
            </button>
          ))}
        </div>
      )}

      {/* Header */}
      <div style={{
        background: `linear-gradient(180deg, ${paper.bgDark} 0%, ${paper.bg} 100%)`,
        borderBottom: `2px solid ${paper.borderMed}`, padding: '16px 24px',
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}>
        <button onClick={() => navigate('/cover')} style={iconBtnStyle()}>
          <ArrowLeft size={16} />
          <span>Close Book</span>
        </button>

        <div style={{ flex: 1, textAlign: 'center', minWidth: 160 }}>
          <h1 style={{ fontFamily: fonts.cursive, fontSize: 28, color: paper.ink }}>
            Table of Contents
          </h1>
        </div>

        <button
          onClick={() => isSubscriber ? setImportModal(true) : setUpgradeModal({})}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
            background: isSubscriber ? paper.ink : paper.borderMed,
            color: paper.bgWhite, border: 'none', borderRadius: 6,
            cursor: 'pointer', fontFamily: fonts.serif, fontSize: 14,
          }}
        >
          {!isSubscriber && <Lock size={14} />}
          <Plus size={14} />
          Add Recipe
        </button>
      </div>

      {/* Search */}
      <div style={{ padding: '16px 24px 8px', maxWidth: 720, margin: '0 auto', width: '100%' }}>
        <div style={{ position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: paper.inkFaint }} />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search recipes or tags…"
            style={{
              width: '100%', padding: '10px 12px 10px 36px',
              border: `1px solid ${paper.borderMed}`, borderRadius: 8,
              background: paper.bgWhite, fontFamily: fonts.serif, fontSize: 16,
              color: paper.ink, outline: 'none',
            }}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer' }}>
              <X size={14} color={paper.inkFaint} />
            </button>
          )}
        </div>
      </div>

      {/* Recipe cards */}
      <div style={{ flex: 1, padding: '8px 24px 32px', maxWidth: 720, margin: '0 auto', width: '100%' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: paper.inkFaint, fontFamily: fonts.serif, fontSize: 16 }}>
            Loading recipes…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: paper.inkFaint }}>
            <BookOpen size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
            <p style={{ fontFamily: fonts.serif, fontSize: 18 }}>No recipes found</p>
          </div>
        ) : (
          filtered.map((recipe, idx) => (
            <RecipeCard
              key={recipe.id}
              recipe={recipe}
              index={idx}
              isLocked={!isSubscriber && idx > 0}
              onFavorite={toggleFavorite}
              onClick={() => handleCardClick(recipe, idx)}
            />
          ))
        )}
      </div>

      {upgradeModal !== null && (
        <UpgradeModal
          recipeTitle={upgradeModal?.recipeTitle}
          onClose={() => setUpgradeModal(null)}
        />
      )}
      {importModal && (
        <ImportModal
          onClose={() => setImportModal(false)}
          onImported={() => { setImportModal(false); loadRecipes() }}
        />
      )}
    </div>
  )
}

function RecipeCard({ recipe, index, isLocked, onFavorite, onClick }) {
  const [hovered, setHovered] = useState(false)

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', marginBottom: 12, borderRadius: 8, overflow: 'hidden',
        border: `1px solid ${paper.borderMed}`,
        boxShadow: hovered ? shadows.cardHover : shadows.card,
        transform: hovered ? 'translateY(-2px)' : 'none',
        transition: 'all 0.2s ease', cursor: 'pointer', background: paper.bgWhite,
        animation: `slideIn 0.4s ease ${index * 0.06}s both`,
        position: 'relative', opacity: isLocked ? 0.85 : 1,
      }}
    >
      {/* Illustration panel */}
      <div style={{
        width: 176, flexShrink: 0,
        background: `linear-gradient(135deg, #f0e8d8, #e0d4c0)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 8,
      }}>
        <FoodIllustration type={recipe.illustration} size={120} />
      </div>

      {/* Content panel */}
      <div style={{ flex: 1, padding: '14px 16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ fontFamily: fonts.cursive, fontSize: 22, color: paper.ink, marginBottom: 6, lineHeight: 1.2 }}>
            {recipe.title}
          </h2>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
            {(recipe.tags || []).map(tag => (
              <span key={tag} style={{
                padding: '2px 8px', background: paper.bgDark,
                border: `1px solid ${paper.borderMed}`, borderRadius: 10,
                fontFamily: fonts.serif, fontSize: 12, color: paper.inkLight,
              }}>{tag}</span>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontFamily: fonts.serif, fontSize: 13, color: paper.inkFaint }}>
            {recipe.servings} servings · {(recipe.steps || []).length} steps
          </span>
          <span style={{ fontFamily: fonts.serif, fontSize: 12, color: paper.borderDark, fontStyle: 'italic' }}>
            p. {(index + 1) * 4 + 12}
          </span>
        </div>
      </div>

      {/* Favorite star */}
      <button
        onClick={(e) => onFavorite(recipe.id, e)}
        style={{
          position: 'absolute', top: 10, right: 10, background: 'none',
          border: 'none', cursor: 'pointer', padding: 4,
        }}
      >
        <Star size={18} fill={recipe.favorite ? leather.gold : 'none'} color={recipe.favorite ? leather.gold : paper.borderMed} />
      </button>

      {/* Lock overlay */}
      {isLocked && (
        <div style={{
          position: 'absolute', inset: 0,
          background: 'rgba(244,239,230,0.6)', backdropFilter: 'blur(1px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: 8,
        }}>
          <div style={{
            background: paper.ink, borderRadius: 8, padding: '8px 16px',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <Lock size={14} color={leather.gold} />
            <span style={{ fontFamily: fonts.serif, fontSize: 14, color: leather.goldLight }}>
              Subscribe to unlock
            </span>
            <Crown size={14} color={leather.gold} />
          </div>
        </div>
      )}
    </div>
  )
}

function iconBtnStyle() {
  return {
    display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px',
    background: 'none', border: `1px solid ${paper.borderMed}`,
    borderRadius: 6, cursor: 'pointer', fontFamily: fonts.serif, fontSize: 14,
    color: paper.inkLight,
  }
}
